/**
 * Common file size validation utility.
 * Validates that the combined total size of all files across multiple upload fields
 * does not exceed the maximum allowed size (default: 50 MB).
 */

export const MAX_TOTAL_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

/**
 * Checks if the combined size of all provided file arrays exceeds the max total file size.
 * Pass ALL file arrays from the form to validate the total across all upload fields.
 *
 * @param fileArrays - One or more file arrays to sum
 * @param maxSizeBytes - Maximum total size in bytes (default: 50 MB)
 * @returns true if total size exceeds the limit
 *
 * @example
 * // In a component with multiple file upload fields:
 * this.isTotalSizeExceeded = checkTotalFileSizeExceeded(
 *   this.claimDocumentsList,
 *   this.mediationAgreementList,
 *   this.proofOfPaymentList,
 *   this.otherDocumentsList
 * );
 */
export function checkTotalFileSizeExceeded(
  ...fileArrays: (File[] | FileList | null | undefined)[]
): boolean {
  return getTotalFileSize(...fileArrays) > MAX_TOTAL_FILE_SIZE_BYTES;
}

/**
 * Returns the total size in bytes of all files across multiple arrays.
 */
export function getTotalFileSize(
  ...fileArrays: (File[] | FileList | null | undefined)[]
): number {
  let total = 0;
  for (const files of fileArrays) {
    if (!files) continue;
    if (files instanceof FileList) {
      for (let i = 0; i < files.length; i++) {
        total += files[i].size;
      }
    } else {
      for (const f of files) {
        total += f.size;
      }
    }
  }
  return total;
}
